import axios from 'axios';
import { useLoadingOverlay } from '../Context/LoadingOverlayContext';
import { useRef, useEffect } from 'react';
import { SecureStorage } from '../Utilities/StorageEncryption';

const useAxiosCall = () => {
    const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
    let cancelTokenSources = useRef({});
    
    // Array of URLs for which cancel token should be handled
    const cancelTokenUrls = ['getDataListing/resident','getDataListing/owner', 'getDataListing/prospect', 'getDataListing/vendor', 'listMessages'];

    const makeApiCall = async ({ requestData, onSuccess, onError, requestType, url, responseType, loader = true, multipart = false, skipLoaderHide = false }) => {
        if (loader) {
            showLoadingOverlay();
        }

        try {
            if (cancelTokenUrls.includes(url) && cancelTokenSources.current[url]) {
                cancelTokenSources.current[url].cancel('Cancelled due to another request');
            }

            if (cancelTokenUrls.includes(url)) {
                cancelTokenSources.current[url] = axios.CancelToken.source();
            }

            const tokenKey = JSON.parse(SecureStorage.getItem('tokenKey'));
            const headers = {
                'Content-Type': (multipart)?'multipart/form-data':'application/json',
            };

            if (tokenKey) {
                headers.Authorization = `Bearer ${tokenKey.value}`;
            }

            const axiosConfig = {
                headers,
                responseType: responseType || 'json',
                cancelToken: cancelTokenUrls.includes(url) ? cancelTokenSources.current[url].token : undefined,
            };

            let response;

            if (requestType === 'GET') {
                response = await axios.get(process.env.REACT_APP_API_BASE_URL + url, axiosConfig);
            } else if (requestType === 'POST') {
                response = await axios.post(process.env.REACT_APP_API_BASE_URL + url, requestData, axiosConfig);
            } else {
                // Handle unsupported request types
                throw new Error(`Unsupported request type: ${requestType}`);
            }

            if (response.data.success === false) {
                onError(response.data.message);
            } else {
                onSuccess(response.data, response.headers);
            }
        } catch (error) {       
            if (axios.isCancel(error)) {
                throw new Error('Request canceled');
            } else if (error.response && error.response.status === 402) {
                const message = error.response.data?.message || "You've been logged out. Your User was logged in on another device or browser. To avoid this, only log in with one device or browser at a time.";
                window.dispatchEvent(new Event('logoutRequired'));
                onError(message);
            } else {
                onError('An error occurred. Please try again.');
            }
        } finally {
            if (!skipLoaderHide) {
                hideLoadingOverlay();
            }
        }
    };

    const refreshToken = async () =>  {
        try {
            const refreshTokenValue = SecureStorage.getItem('refreshToken');
            const tokenKey = JSON.parse(SecureStorage.getItem('tokenKey'));
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}refreshToken`, {
                    refreshToken: refreshTokenValue,
                    token: tokenKey.value
                }
            );
            const tokenData = {
                value: response.data.data.token,
                timestamp: new Date().getTime(),
            };          
            SecureStorage.setItem('tokenKey', JSON.stringify(tokenData))
            SecureStorage.setItem('refreshToken', response.data.data.refreshToken);

            return response.data.data.token;
        } catch (error) {
            // Refresh token failed
        }
    }
    useEffect( () => { 
      const interceptor = axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            if(error.response.status === 401 && !originalRequest._retry) {
                if(error.response.data.Code=='Token_Expired'){
                    window.location.href='/login';
                }
                originalRequest._retry = true;
                const newToken = await refreshToken();
                if(newToken) {
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } else {
                    // Token refresh failed
                }
            }
            return Promise.reject(error)
        }
      );
    
      return () => {
        axios.interceptors.response.eject(interceptor);
      }
    }, [])
    

    return { makeApiCall };
};

export default useAxiosCall;
